import { generators, nativeSequenceCommandSchema, nativeSequenceReplySchema, nativeSequenceEventSchema,
  type NativeSequenceCommand, type NativeSequenceEvent } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { GraphHostMessage, NodeServiceGraphChannel } from '../../services/macro-graph/NodeServiceGraphChannel';

export interface IInteractiveGraphTargets { owns(targetSessionId: string): boolean }
/** This class forwards validated native leaves. It contains no graph routes or author JavaScript. */
export class InteractiveSequenceRelay {
  private readonly pending = new Map<string, NativeSequenceCommand>();
  private readonly bound = new Map<string, string>();
  private readonly handles = new Map<string, string>();
  private readonly watches = new Map<string, string>();
  private readonly cleanupRequests = new Set<string>();
  private unsubscribe: (() => void) | null = null;
  private disconnected: (() => void) | null = null;
  private lastEpoch: string | null = null;
  private nativeOnline = true;
  private readonly cleanupTargets = new Map<string, number>();
  public constructor(private readonly node: NodeServiceGraphChannel, private readonly native: IIpcService,
    private readonly targets: IInteractiveGraphTargets) {}
  public initialize(): void {
    if (this.unsubscribe) return;
    this.unsubscribe = this.node.subscribe(message => this.fromNode(message));
    this.disconnected = this.node.onConnection(connected => { if (!connected) this.revokeAll(); });
  }
  /** Trusted main/API has proved the previous principal; permit only native revocation during its receipt. */
  public async cleanupPrevious<T>(target: string, operation: () => Promise<T>): Promise<T> {
    this.cleanupTargets.set(target, (this.cleanupTargets.get(target) ?? 0) + 1);
    try { return await operation(); } finally {
      const remaining = (this.cleanupTargets.get(target) ?? 1) - 1;
      if (remaining) this.cleanupTargets.set(target, remaining); else this.cleanupTargets.delete(target);
    }
  }
  public receiveNative(message: GraphHostMessage): boolean {
    if (message.header.target !== 'process' || !['macroSequence', 'macroSequenceEvent'].includes(String(message.header.action))) return false;
    if (!message.header.isReply) return true;
    if (message.header.action === 'macroSequenceEvent') {
      const parsed = nativeSequenceEventSchema.safeParse(message.body.message); if (!parsed.success) return true;
      const target = this.eventTarget(parsed.data);
      if (target && this.bound.has(target) && this.targets.owns(target)) this.node.send(generators.process.macroSequenceEvent(parsed.data));
      return true;
    }
    const parsed = nativeSequenceReplySchema.safeParse(message.body.message); if (!parsed.success) return true;
    const reply = parsed.data;
    if (this.cleanupRequests.delete(reply.requestId)) return true;
    const command = this.pending.get(reply.requestId); if (!command) return true;
    this.pending.delete(reply.requestId);
    if (reply.kind === 'capabilities') { this.lastEpoch = reply.hostEpoch; this.nativeOnline = true; }
    if (reply.kind === 'target-bound' && command.command === 'bind-target' && reply.targetSessionId === command.targetSessionId
      && this.targets.owns(reply.targetSessionId)) this.bound.set(reply.targetSessionId, reply.hostEpoch);
    if (reply.kind === 'target-revoked') this.clearTarget(reply.targetSessionId);
    if (reply.kind === 'prepared' && command.command === 'prepare') this.handles.set(reply.prepared.handle, command.targetSessionId);
    if (reply.kind === 'error' && command.command === 'observe') this.watches.delete(command.watchId);
    if (reply.kind === 'released' && command.command === 'release') { command.handles.forEach(handle => this.handles.delete(handle)); command.watchIds.forEach(watch => this.watches.delete(watch)); }
    this.node.send(generators.process.macroSequenceReply(reply)); return true;
  }
  public nativeDisconnected(): void {
    this.nativeOnline = false;
    for (const command of this.pending.values()) this.reject(command, 'native_relay_disconnected_unknown');
    this.pending.clear();
    for (const targetSessionId of this.bound.keys()) this.node.send(generators.service.macroGraphSession({ version: 1,
      command: 'revoke', requestId: crypto.randomUUID(), targetSessionId }));
    this.bound.clear(); this.handles.clear(); this.watches.clear();
  }
  public release(target: string): void {
    const epoch = this.bound.get(target); if (!epoch) return;
    const requestId = crypto.randomUUID(); this.cleanupRequests.add(requestId);
    this.native.send(generators.process.macroSequence({ command: 'revoke-target', requestId, targetSessionId: target, hostEpoch: epoch }));
    this.clearTarget(target);
  }
  public dispose(): void { this.revokeAll(); this.unsubscribe?.(); this.disconnected?.(); this.unsubscribe = null; this.disconnected = null; }
  private fromNode(message: GraphHostMessage): void {
    if (message.header.target !== 'process' || message.header.action !== 'macroSequence' || message.header.isReply) return;
    const parsed = nativeSequenceCommandSchema.safeParse(message.body.message); if (!parsed.success) return;
    const command = parsed.data;
    if (this.pending.has(command.requestId)) return;
    if (!this.nativeOnline && command.command !== 'capabilities') { this.reject(command, 'native_relay_disconnected_unknown'); return; }
    if (!this.authorized(command)) { this.reject(command, 'native_relay_target_not_owned'); return; }
    this.pending.set(command.requestId, command);
    if (command.command === 'observe') {
      const target = this.handles.get(command.prepared[0].handle); if (target) this.watches.set(command.watchId, target);
    }
    this.native.send(generators.process.macroSequence(command));
  }
  private reject(command: NativeSequenceCommand, code: string): void {
    if (this.lastEpoch) this.node.send(generators.process.macroSequenceReply({ kind: 'error', requestId: command.requestId,
      hostEpoch: this.lastEpoch, code, required: null, available: null }));
  }
  private authorized(command: NativeSequenceCommand): boolean {
    if (command.command === 'capabilities') return true;
    if (command.command === 'revoke-target' && this.cleanupTargets.has(command.targetSessionId)) return true;
    if (command.command === 'bind-target') return this.targets.owns(command.targetSessionId);
    if (command.command === 'revoke-target' || command.command === 'prepare' || command.command === 'environment') return this.bound.has(command.targetSessionId) && this.targets.owns(command.targetSessionId);
    if (command.command === 'execute') return this.handles.get(command.prepared.handle) === command.identity.targetSessionId
      && this.targets.owns(command.identity.targetSessionId) && this.bound.get(command.identity.targetSessionId) === command.prepared.hostEpoch;
    if (command.command === 'observe') return command.prepared.every(handle => this.owned(this.handles.get(handle.handle)));
    // Cleanup may race acknowledgement or repeat after map retirement. Native enforces exact socket ownership.
    if (command.command === 'release') return true;
    // Only the trusted service emits these. Native verifies the retained execution's socket owner.
    // Keeping old execution IDs here would make a long-running graph grow without bound.
    return command.command === 'status' || command.command === 'cancel';
  }
  private owned(target: string | undefined): boolean { return !!target && this.targets.owns(target); }
  private eventTarget(event: NativeSequenceEvent): string | undefined {
    return event.kind === 'terminal' ? event.terminal.identity.targetSessionId : event.kind === 'progress' ? event.identity.targetSessionId
      : event.kind === 'gates-confirmed' || event.kind === 'invalidated' || event.kind === 'watch-state' ? event.targetSessionId : this.watches.get(event.watchId);
  }
  private clearTarget(target: string): void {
    this.bound.delete(target);
    for (const [id, candidate] of this.handles) if (candidate === target) this.handles.delete(id);
    for (const [id, candidate] of this.watches) if (candidate === target) this.watches.delete(id);
  }
  private revokeAll(): void { for (const target of [...this.bound.keys()]) this.release(target); this.pending.clear(); }
}
